<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { IconName } from '@/libs/icons';

withDefaults(
  defineProps<{
    /** Lucide icon name */
    icon?: IconName;
    size?: 'medium' | 'large';
    type?: 'primary' | 'secondary' | 'outline' | 'ghost';
  }>(),
  {
    icon: undefined,
    size: 'medium',
    type: 'secondary',
  },
);
</script>

<template>
  <button
    class="rounded-button"
    :class="[`rounded-button--${type}`, `rounded-button--${size}`]"
    type="button"
  >
    <BaseIcon v-if="icon" :name="icon" class="rounded-button__icon" />
    <slot />
  </button>
</template>

<style lang="scss">
.rounded-button {
  align-items: center;
  border: none;
  border-radius: $radius-pill;
  color: $color-neutral-highest;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 0;

  &--medium {
    height: 40px;
    width: 40px;

    .rounded-button__icon {
      font-size: 20px;
    }
  }

  &--large {
    height: 52px;
    width: 52px;

    .rounded-button__icon {
      font-size: 24px;
    }
  }

  &--primary {
    background-color: $color-neutral-highest;
    color: $color-white;

    &:active {
      background-color: rgba($color-neutral-highest, 0.9);
    }
  }

  &--secondary {
    background-color: $color-neutral-lower;

    &:active {
      background-color: $color-neutral-low;
    }
  }

  &--outline {
    background-color: $color-white;
    border: 1px solid $color-border-medium;

    &:active {
      background-color: $color-neutral-low;
    }
  }

  &--ghost {
    background-color: transparent;

    &:active {
      background-color: $color-neutral-low;
    }
  }
}
</style>
